/**
 * A set page's markup layer, as the markup client reaches it. The viewer saves a set page through
 * `api/markup.ts` like any file; the markup client hands a set page's file id
 * (`attachment-page:{set}:{page}:{geometry}`) to this module, so the viewer never knows about attachment sets and only this client
 * talks to the set routes.
 *
 * The layer's routes keep the shapes of the markup set routes, so the viewer and its outbox are
 * unchanged: the layer is always the caller's own `draft`, one per page, it takes neither a
 * revision nor a section group, and its first save carries `expected_version` 0. The set's `id`
 * is the file id, so a clear can find the page again.
 *
 * A save carries the page's `geometry` (its rotation, crop and replacement): the one in the file
 * id, which the host took from the page map the layer was drawn on, never one read at save time. A
 * page changed underneath answers 409 `page_geometry_changed` and the viewer reloads that page's
 * layer (a new file id, at the current geometry) instead of drawing on a page that is no longer the
 * one on screen. A page file id without a geometry is no set page: there is nothing to fence with.
 *
 * A Clear names the layer version this tab last saw (`expected_version`): the one its last read
 * showed, its last save returned or its last Clear answered, 0 for a page never drawn on (a Clear
 * of a layer that holds nothing writes nothing, whatever it names). A co-worker's save since then
 * answers 409 with `current_version`, so a Clear never empties markups the person has not seen;
 * the set's screens hear of it (`onSetMarkupChangedElsewhere`) and the viewer reads the layer
 * again. A cleared layer reads with its version and no shapes, and a Clear answers that version: a
 * save that names no version (the viewer's first after it read nothing, or after it cleared) names
 * the one this tab last saw, so drawing after a Clear is one PUT. A save drawn before the clear,
 * a lost first save retried included, is stale (409), and the viewer merges it as the writer's
 * own addition.
 */
import { isAxiosError } from "axios";

import axios from "../../config/axios";
import { currentTenantContentScopeKey } from "../../contexts/tenantScopeHeaders";
import {
    MARKUP_REQUEST_TIMEOUT_MS,
    type ApiMarkupSaveResult,
    type ApiMarkupSet,
    type SaveMarkupBody,
} from "../markup";
import { attachmentSetConflictVersion, attachmentSetErrorCode } from "./refusals";
import { pageMarkupRoute, parseSetFileId, snapshotPictureRoute } from "./routes";
import type { ClearedLayer } from "./types";

/**
 * The layer version this tab last saw, by tenant and page; a page it never read is absent. The
 * tenant is part of the key: set and page ids collide across tenants, and a tab can switch
 * project (CLAUDE_SHARED.md, Multi-tenant).
 */
const seenVersions = new Map<string, number>();
const seenKey = (setId: string, pageId: string) =>
    JSON.stringify([currentTenantContentScopeKey(), setId, pageId]);

type MarkupChangedListener = (setId: string) => void;
const markupChangedListeners = new Set<MarkupChangedListener>();

/**
 * Calls `listener` when a Clear was refused because the page's markups changed since this tab read
 * them (409 with `current_version`); returns the unsubscribe. The screen says so; the viewer reads
 * the layer again on its own.
 */
export function onSetMarkupChangedElsewhere(listener: MarkupChangedListener): () => void {
    markupChangedListeners.add(listener);
    return () => {
        markupChangedListeners.delete(listener);
    };
}

type MarkupSavedListener = (setId: string) => void;
const markupSavedListeners = new Set<MarkupSavedListener>();

/**
 * Tells the section's screens that a page's markup layer was written to the server (a save or a
 * clear): the set's `markup_count`s changed under any cached copy. The layer calls it once the
 * server has accepted the write.
 */
export function notifySetMarkupSaved(setId: string): void {
    markupSavedListeners.forEach((listener) => listener(setId));
}

/** Calls `listener` after each layer write the server accepted; returns the unsubscribe. */
export function onSetMarkupSaved(listener: MarkupSavedListener): () => void {
    markupSavedListeners.add(listener);
    return () => {
        markupSavedListeners.delete(listener);
    };
}

type PageChangedListener = (setId: string) => void;
const pageChangedListeners = new Set<PageChangedListener>();

/**
 * Calls `listener` when a save found its page rotated, cropped or replaced underneath it (409
 * `page_geometry_changed`); returns the unsubscribe. The host then reads the page map again, the
 * page's file id moves to the new geometry, and the viewer reads that page's layer afresh.
 */
export function onAttachmentPageChanged(listener: PageChangedListener): () => void {
    pageChangedListeners.add(listener);
    return () => {
        pageChangedListeners.delete(listener);
    };
}

/** A save refused because the page under the layer was changed since the layer was drawn. */
export class AttachmentPageChanged extends Error {
    constructor(
        readonly setId: string,
        readonly pageId: string,
        readonly geometry: number | null,
    ) {
        super("This page changed since these markups were drawn. Its markups were reloaded.");
        this.name = "AttachmentPageChanged";
    }
}

/** A Clear refused because the page's markups changed since this tab read them. */
export class SetMarkupChanged extends Error {
    constructor(
        readonly setId: string,
        readonly currentVersion: number,
    ) {
        super("These markups were changed somewhere else since they were read.");
        this.name = "SetMarkupChanged";
    }
}

/** The layer as the viewer holds a markup set: addressed by the file id, page 0, its own. */
function asViewerSet(fileId: string, set: ApiMarkupSet): ApiMarkupSet {
    return { ...set, id: fileId, file_id: fileId, page: 0, is_own: true };
}

/** One set page's layer: what the markup client does with a set page's file id. */
export interface SetMarkupLayer {
    /** A set page's layer stores image snapshots (MARKUP_SCHEMA.md, server 1.3.0). */
    readonly storesSnapshots: true;
    /** The caller's layer on the page, as the viewer's markup sets; notes the page's geometry. */
    read(): Promise<ApiMarkupSet[]>;
    /** Saves the layer (a sent revision refuses), then tells the section's screens. */
    save(body: SaveMarkupBody): Promise<ApiMarkupSaveResult>;
    /**
     * Empties the layer: Clear, naming the version this tab last saw. A layer already gone is not
     * an error; one changed since this tab read it rejects with {@link SetMarkupChanged}.
     */
    clear(): Promise<void>;
    /**
     * The picture of the saved snapshot `shapeId` on this layer, or null when there is none
     * for this reader (404: not a saved snapshot here, its region hidden from them, or the page
     * hidden or gone).
     */
    snapshotPicture(shapeId: string): Promise<Blob | null>;
}

/** The layer of the set page `fileId` addresses, or null when it is any other file. */
export function setMarkupLayer(fileId: string): SetMarkupLayer | null {
    const address = parseSetFileId(fileId);
    if (!address) return null;
    const url = pageMarkupRoute(address);
    const page = () => seenKey(address.setId, address.pageId);
    return {
        storesSnapshots: true,
        read: async () => {
            const sets = (
                await axios.get<ApiMarkupSet[]>(url, { timeout: MARKUP_REQUEST_TIMEOUT_MS })
            ).data;
            // A page never drawn on reads as none: a Clear of it names 0, as a first save does.
            seenVersions.set(page(), sets[0]?.version ?? 0);
            return sets.map((set) => asViewerSet(fileId, set));
        },
        save: async (body) => {
            const { revision_id: _revision, section_group_id: _group, ...layerBody } = body;
            try {
                const resp = await axios.put<ApiMarkupSaveResult>(
                    url,
                    {
                        ...layerBody,
                        // The viewer names none for its first save after it read nothing or
                        // cleared: the version this tab last saw is the layer's then.
                        expected_version: body.expected_version ?? seenVersions.get(page()) ?? 0,
                        geometry: address.geometry,
                    },
                    { timeout: MARKUP_REQUEST_TIMEOUT_MS },
                );
                seenVersions.set(page(), resp.data.version);
                notifySetMarkupSaved(address.setId);
                return { ...asViewerSet(fileId, resp.data), delivery: resp.data.delivery };
            } catch (error) {
                if (
                    isAxiosError(error) &&
                    error.response?.status === 409 &&
                    attachmentSetErrorCode(error) === "page_geometry_changed"
                ) {
                    const detail = (error.response.data as { detail?: { geometry?: unknown } })
                        .detail;
                    pageChangedListeners.forEach((listener) => listener(address.setId));
                    throw new AttachmentPageChanged(
                        address.setId,
                        address.pageId,
                        typeof detail?.geometry === "number" ? detail.geometry : null,
                    );
                }
                throw error;
            }
        },
        clear: async () => {
            let version = 0;
            try {
                const resp = await axios.delete<ClearedLayer | undefined>(url, {
                    params: { expected_version: seenVersions.get(page()) ?? 0 },
                    timeout: MARKUP_REQUEST_TIMEOUT_MS,
                });
                // The layer's version after the clear, which the next save names.
                if (typeof resp?.data?.version === "number") version = resp.data.version;
            } catch (err) {
                const current = attachmentSetConflictVersion(err);
                if (current !== null) {
                    markupChangedListeners.forEach((listener) => listener(address.setId));
                    throw new SetMarkupChanged(address.setId, current);
                }
                // The layer is already gone: a Clear is idempotent.
                if (!(isAxiosError(err) && err.response?.status === 404)) throw err;
            }
            seenVersions.set(page(), version);
            notifySetMarkupSaved(address.setId);
        },
        snapshotPicture: async (shapeId) => {
            try {
                return (
                    await axios.get<Blob>(snapshotPictureRoute(address, shapeId), {
                        responseType: "blob",
                    })
                ).data;
            } catch (err) {
                if (isAxiosError(err) && err.response?.status === 404) return null;
                throw err;
            }
        },
    };
}

/**
 * The picture of the saved image snapshot `shapeId` on the layer of the set page `fileId`
 * addresses, as this reader may read its region; null when there is none (or the file is no set
 * page). A host's page map hands it to the viewer (`DocumentPageMap.snapshotPicture`).
 */
export async function snapshotPictureOf(fileId: string, shapeId: string): Promise<Blob | null> {
    return (await setMarkupLayer(fileId)?.snapshotPicture(shapeId)) ?? null;
}
